import { Group, Stack, Text, Tooltip } from '@mantine/core';

import { COST_CATEGORY_LABELS, type CostCategory, type ProviderCost } from '@/types/economics';
import { providerLabel } from '@/types/provider-cost';
import { formatAmount, SINGLE_SERIES_COLOR } from './palette';

export interface CostByProviderChartProps {
  providers: ProviderCost[];
  currency: string;
}

const BAR = 12;
/** Room kept at the end of the row for the value at a bar's tip. */
const LABEL_ROOM = 120;

function nameOf(provider: string): string {
  // Costs with no provider are grouped under their category (partner fees).
  return COST_CATEGORY_LABELS[provider as CostCategory] ?? providerLabel(provider);
}

/**
 * Cost by provider, largest first: one series in one colour, the value at
 * each bar's tip. A provider whose usage is all unrated shows that in words,
 * never as an empty bar that reads as free.
 */
export function CostByProviderChart({ providers, currency }: Readonly<CostByProviderChartProps>) {
  const max = Math.max(0, ...providers.map((p) => p.cost));
  if (providers.length === 0) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="lg">
        No costs in this period.
      </Text>
    );
  }

  return (
    <Stack gap={10} role="list" aria-label={`Cost by provider, in ${currency}`}>
      {providers.map((p) => {
        const share = max > 0 ? p.cost / max : 0;
        const value = formatAmount(p.cost, currency);
        const allUnrated = p.cost === 0 && p.unrated > 0;
        return (
          <Tooltip
            key={p.provider}
            withArrow
            label={`${nameOf(p.provider)}: ${value} over ${p.lines} line(s)${
              p.unrated ? `, ${p.unrated} unrated and not counted` : ''
            }`}
          >
            <div role="listitem" data-testid={`provider-${p.provider}`}>
              <Group gap="sm" wrap="nowrap">
                <Text size="sm" w={150} truncate="end">
                  {nameOf(p.provider)}
                </Text>
                {allUnrated ? (
                  <Text size="xs" c="dimmed">
                    {p.unrated} unrated line(s) — no rate yet
                  </Text>
                ) : (
                  <Group gap={8} wrap="nowrap" style={{ flex: 1 }}>
                    <div
                      style={{
                        // A share of the room left after the value label, so
                        // every bar stays in proportion however narrow the row.
                        width: `calc((100% - ${LABEL_ROOM}px) * ${Math.max(share, 0.005)})`,
                        flexShrink: 0,
                        height: BAR,
                        backgroundColor: SINGLE_SERIES_COLOR,
                        borderRadius: '0 4px 4px 0',
                      }}
                    />
                    <Text
                      size="xs"
                      style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                    >
                      {value}
                      {p.unrated ? ` + ${p.unrated} unrated` : ''}
                    </Text>
                  </Group>
                )}
              </Group>
            </div>
          </Tooltip>
        );
      })}
    </Stack>
  );
}
